import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "新闻", to: "/news" },
  { label: "影视作品", to: "/works" },
  { label: "写真图集", to: "/gallery" },
  { label: "专题", to: "/special" },
  { label: "关于", to: "/about" },
];

const quickKeywords = ["南方来信", "忘川", "写真", "公益"];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const searchPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (searchOpen) {
      const timer = window.setTimeout(() => inputRef.current?.focus(), 80);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [searchOpen]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSearchOpen(false);
        setMobileOpen(false);
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    const onClickOutside = (event: MouseEvent) => {
      if (searchPanelRef.current && !searchPanelRef.current.contains(event.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, []);

  const submitSearch = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setSearchOpen(false);
      return;
    }
    setSearchOpen(false);
    setKeyword("");
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
  };

  const currentPath = location.pathname;
  const isActive = (to: string) => (to === "/" ? currentPath === "/" : currentPath.startsWith(to));

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-all duration-300 ${
        scrolled
          ? "border-background-200 bg-background-50/95 backdrop-blur-md"
          : "border-transparent bg-background-50"
      }`}
    >
      <div className="flex h-16 w-full items-center justify-between gap-6 px-4 md:h-[72px] md:px-8 lg:px-12">
        <Link to="/" className="group flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50 md:h-10 md:w-10">
            <i className="ri-vip-crown-2-line text-lg" aria-hidden="true"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-[17px] font-semibold tracking-wide text-foreground-950 md:text-lg">
              张柏芝
            </span>
            <span className="mt-1 text-[10px] uppercase tracking-[0.3em] text-foreground-500">
              CECILIA CHEUNG
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="全站主导航">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`relative rounded-md px-3.5 py-2 text-sm transition-colors ${
                isActive(link.to)
                  ? "text-primary-700"
                  : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              {link.label}
              <span
                className={`absolute bottom-0.5 left-1/2 h-[2px] -translate-x-1/2 rounded-full bg-primary-500 transition-all duration-300 ${
                  isActive(link.to) ? "w-5 opacity-100" : "w-0 opacity-0"
                }`}
              ></span>
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="relative" ref={searchPanelRef}>
            <button
              type="button"
              onClick={() => setSearchOpen((open) => !open)}
              aria-label="搜索"
              aria-expanded={searchOpen}
              className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-full transition-colors md:h-10 md:w-10 ${
                searchOpen
                  ? "bg-primary-100 text-primary-700"
                  : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
              }`}
            >
              <i className="ri-search-line text-base" aria-hidden="true"></i>
            </button>

            {searchOpen && (
              <div className="absolute right-0 top-12 w-[min(92vw,380px)] animate-soft-in rounded-lg border border-background-200 bg-background-50 p-4 md:top-14">
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    submitSearch(keyword);
                  }}
                >
                  <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2 focus-within:border-primary-400">
                    <i className="ri-search-line text-sm text-foreground-500" aria-hidden="true"></i>
                    <input
                      ref={inputRef}
                      value={keyword}
                      onChange={(event) => setKeyword(event.target.value)}
                      placeholder="搜索新闻、作品或写真"
                      aria-label="搜索关键词"
                      className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                    />
                    <button
                      type="submit"
                      className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 py-1 text-xs text-background-50 transition-colors hover:bg-primary-600"
                    >
                      搜索
                    </button>
                  </div>
                </form>
                <div className="mt-3">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-foreground-400">热门关键词</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {quickKeywords.map((item) => (
                      <button
                        key={item}
                        type="button"
                        onClick={() => submitSearch(item)}
                        className="cursor-pointer rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
                <p className="mt-3 text-[11px] text-foreground-400">
                  支持新闻、影视作品与写真图集的全站检索（演示数据）
                </p>
              </div>
            )}
          </div>

          <Link
            to="/special/s001"
            className="hidden cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-xs text-background-50 transition-colors hover:bg-primary-600 md:inline-flex md:items-center md:gap-1.5"
          >
            <i className="ri-sparkling-line" aria-hidden="true"></i>
            年度专题
          </Link>

          <button
            type="button"
            onClick={() => setMobileOpen((open) => !open)}
            aria-label={mobileOpen ? "关闭菜单" : "打开菜单"}
            aria-expanded={mobileOpen}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-background-100 text-foreground-800 lg:hidden"
          >
            <i className={mobileOpen ? "ri-close-line text-lg" : "ri-menu-3-line text-lg"} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="animate-soft-in border-t border-background-200 bg-background-50 px-4 pb-6 pt-3 lg:hidden">
          <nav className="flex flex-col" aria-label="移动端导航">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`flex items-center justify-between border-b border-background-100 py-3 text-sm ${
                  isActive(link.to) ? "text-primary-700" : "text-foreground-800"
                }`}
              >
                {link.label}
                <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
              </Link>
            ))}
          </nav>
          <Link
            to="/special/s001"
            className="mt-4 flex cursor-pointer items-center justify-center gap-2 rounded-md bg-primary-500 py-2.5 text-sm text-background-50"
          >
            <i className="ri-sparkling-line" aria-hidden="true"></i>
            查看年度专题
          </Link>
        </div>
      )}
    </header>
  );
}